/**
 * Email parsing regex that supports multi-part TLDs
 * This regex can handle TLDs like .com, .co.uk, .com.au, .org.uk, etc.
 * 
 * Pattern explanation:
 * \b([a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,})\b - Matches email addresses with multi-part TLDs
 * \b - Word boundary to prevent partial matches
 * [a-zA-Z0-9._%+-]+ - One or more alphanumeric characters, dots, underscores, percent, plus, or hyphens for local part
 * @ - Literal @ symbol
 * [a-zA-Z0-9.-]+ - One or more alphanumeric characters, dots, or hyphens for domain
 * \. - Literal dot before TLD
 * [a-zA-Z]{2,} - Two or more letters for TLD (supports multi-part TLDs)
 * \b - Word boundary to prevent partial matches
 */
export const EMAIL_PARSING_REGEX = /\b([a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,})\b/gim;

/**
 * Converts email addresses in text to clickable mailto links
 * @param {string} text - The text to parse for email addresses
 * @returns {string} - The text with email addresses converted to HTML links
 */
export function convertEmailsToMailToLinks(text) {
  if (!text) {
    return text;
  }
  
  return text.replace(
    EMAIL_PARSING_REGEX,
    '<a style="text-decoration: none;" href="mailto:$1">$1</a>'
  );
}

/**
 * Extracts all email addresses from text
 * @param {string} text - The text to extract emails from
 * @returns {Array<string>} - Array of found email addresses
 */
export function extractEmailsFromText(text) {
  if (!text) {
    return [];
  }
  
  const matches = text.match(EMAIL_PARSING_REGEX);
  return matches || [];
}

/**
 * Validates if a string is a valid email address
 * @param {string} email - The email address to validate
 * @returns {boolean} - True if valid, false otherwise
 */
export function isValidEmail(email) {
  if (!email) {
    return false;
  }
  
  const trimmed = email.toString().trim();
  
  // Basic structure check
  if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(trimmed)) {
    return false;
  }
  
  // More specific validation using the same pattern as EMAIL_PARSING_REGEX
  return /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/.test(trimmed);
} 